import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  FormControlLabel,
  Checkbox,
  Switch,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  Paper,
  MenuItem,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import {
  doc,
  updateDoc,
  deleteDoc,
  collection,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { db, deleteUser } from "../../config/firebase";
import { splitName, joinName } from "../../utils/name";
import { formatDateTime } from "../../utils/formatDate";
import ModalDialogTitle from "../common/ModalDialogTitle";
import ModalActions from "../common/ModalActions";

const EditArchivedInstructor = ({
  open,
  onClose,
  instructor,
  onSuccess,
  onUserDeleted,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [ext, setExt] = useState("");
  const [notes, setNotes] = useState("");
  const [sccId, setSccId] = useState("");
  const [sinclairEmail, setSinclairEmail] = useState("");
  const [eLearnInstructor, setELearnInstructor] = useState(false);
  const [dentalInstructor, setDentalInstructor] = useState(false);
  const [pltwInstructor, setPltwInstructor] = useState(false);
  const [academicInstructor, setAcademicInstructor] = useState(false);
  const [userTypes, setUserTypes] = useState({
    staff: false,
    coordinator: false,
    instructor: false,
    admin: false,
    pathwayManager: false,
  });
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [partner, setPartner] = useState("");
  const [pathways, setPathways] = useState([]);
  const [pathwaysLoading, setPathwaysLoading] = useState(true);
  const [pathway, setPathway] = useState("");
  const [siteId, setSiteId] = useState("");
  const [active, setActive] = useState("-1");
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const formatPhoneNumber = (value) => {
    if (!value) return "";
    const phoneNumber = value.replace(/\D/g, "");
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
        3,
        6,
      )}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (setter) => (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setter(formatted);
  };

  useEffect(() => {
    if (instructor) {
      if (instructor.firstName || instructor.lastName) {
        setFirstName(instructor.firstName || "");
        setLastName(instructor.lastName || "");
      } else {
        const split = splitName(instructor.name);
        setFirstName(split.firstName);
        setLastName(split.lastName);
      }
      setPhone(instructor.phone ? formatPhoneNumber(instructor.phone) : "");
      setSiteId(
        instructor.SOD_Site_ID ||
          instructor.SOD_SiteId ||
          instructor.siteId ||
          instructor.SiteID ||
          "",
      );
      setPartner(instructor.Partner || instructor.partner || "");
      setPathway(instructor.Pathway || instructor.pathway || "");
      setDateOfBirth(instructor.dateOfBirth || "");
      setExt(instructor.ext || "");
      setNotes(instructor.notes || "");
      setSccId(instructor.SccID || "");
      setSinclairEmail(instructor.sinclair_email || "");
      setELearnInstructor(instructor.eLearn_instructor || false);
      setDentalInstructor(instructor.dental_instructor || false);
      setPltwInstructor(instructor.pltw_instructor || false);
      setAcademicInstructor(instructor.academic_instructor || false);
      setUserTypes({
        staff: instructor.userTypes?.staff || false,
        coordinator: instructor.userTypes?.coordinator || false,
        instructor: instructor.userTypes?.instructor || false,
        admin: instructor.userTypes?.admin || false,
        pathwayManager: instructor.userTypes?.pathwayManager || false,
      });
      setActive(
        instructor.Active !== undefined
          ? String(instructor.Active)
          : instructor.active !== undefined
            ? String(instructor.active)
            : "-1",
      );
    }
  }, [instructor]);

  useEffect(() => {
    const loadSites = async () => {
      try {
        setSitesLoading(true);
        const snap = await getDocs(collection(db, "Sites"));
        const data = snap.docs.map((d) => ({
          id: d.id,
          SiteID: d.data().SiteID,
          SiteName: d.data().SiteName,
          ...d.data(),
        }));
        data.sort((a, b) => (a.SiteName || "").localeCompare(b.SiteName || ""));
        setSites(data);
      } catch (e) {
        console.error("Error loading Sites in EditArchivedInstructor:", e);
        setSites([]);
      } finally {
        setSitesLoading(false);
      }
    };
    loadSites();
  }, []);

  useEffect(() => {
    const loadPathways = async () => {
      try {
        setPathwaysLoading(true);
        const pwSnap = await getDocs(collection(db, "PathwaysPrograms"));
        const pwList = pwSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        pwList.sort((a, b) =>
          (a.PathwayName || "").localeCompare(b.PathwayName || ""),
        );
        setPathways(pwList);
      } catch (e) {
        console.error("Error loading pathways in EditArchivedInstructor:", e);
        setPathways([]);
      } finally {
        setPathwaysLoading(false);
      }
    };
    loadPathways();
  }, []);

  const handleUserTypeChange = (type) => (e) => {
    setUserTypes((prev) => ({ ...prev, [type]: e.target.checked }));
  };

  const handleSiteChange = (e) => {
    setSiteId(e.target.value);
  };
  const handlePartnerChange = (e) => setPartner(e.target.value);
  const handlePathwayChange = (e) => setPathway(e.target.value);

  const handleActiveToggle = (e) => {
    setActive(e.target.checked ? "-1" : "0");
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const userRef = doc(db, "InstructorArchive", instructor.id);
      const updateData = {};
      updateData.firstName = firstName;
      updateData.lastName = lastName;
      updateData.name = joinName(firstName, lastName);
      if (phone !== undefined) updateData.phone = phone;
      if (userTypes !== undefined) updateData.userTypes = userTypes;
      updateData.techPrepAdminFlag = userTypes.admin || false;
      if (dateOfBirth !== undefined) updateData.dateOfBirth = dateOfBirth;
      if (ext !== undefined) updateData.ext = ext;
      if (notes !== undefined) updateData.notes = notes;
      if (sccId !== undefined) updateData.SccID = sccId;
      if (sinclairEmail !== undefined) updateData.sinclair_email = sinclairEmail;
      updateData.eLearn_instructor = eLearnInstructor;
      updateData.dental_instructor = dentalInstructor;
      updateData.pltw_instructor = pltwInstructor;
      updateData.academic_instructor = academicInstructor;
      if (siteId !== undefined) updateData.SOD_Site_ID = siteId;
      if (partner !== undefined) updateData.Partner = partner;
      if (pathway !== undefined) updateData.Pathway = pathway;
      if (active !== undefined) updateData.Active = active;

      if (Object.keys(updateData).length > 0) {
        updateData.updated_at = serverTimestamp();
        await updateDoc(userRef, updateData);
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("Error updating archived instructor (Firestore):", error);
      alert(`Error updating archived instructor: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this archived instructor? This action cannot be undone.",
      )
    ) {
      setDeleteLoading(true);
      try {
        // Best-effort: also clean up the Firebase Auth account for this
        // instructor (if one still exists under this id), so the email
        // can be reused if the instructor is ever added again. The
        // deleteUser callable only touches the "users" collection, not
        // InstructorArchive, so the archive doc still needs its own
        // deleteDoc below regardless of whether this succeeds.
        try {
          const authResult = await deleteUser(instructor.id);
          if (!authResult.success) {
            console.warn(
              "Could not delete Auth account for archived instructor:",
              authResult.message,
            );
          }
        } catch (authError) {
          console.warn(
            "Error deleting Auth account for archived instructor:",
            authError,
          );
        }

        const userRef = doc(db, "InstructorArchive", instructor.id);
        await deleteDoc(userRef);
        if (onUserDeleted) onUserDeleted();
        onClose();
      } catch (error) {
        console.error("Error deleting archived instructor:", error);
        alert("Error deleting archived instructor. Please try again.");
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  // DeliveryDetails records carry created_at/updatedAt Firestore Timestamps
  // (either a real Timestamp instance, or the plain {seconds, nanoseconds}
  // shape) — rendering one of those directly as a child crashes React, so
  // format it to a date string instead of showing it raw like every other
  // field below.
  const formatDeliveryDetailValue = (value) => {
    if (
      value &&
      typeof value === "object" &&
      (typeof value.toDate === "function" || "seconds" in value)
    ) {
      return formatDateTime(value) || "N/A";
    }
    return value || "N/A";
  };

  const renderDeliveryDetails = () => {
    if (
      !instructor.deliveryDetails ||
      instructor.deliveryDetails.length === 0
    ) {
      return (
        <Box p={2}>
          <Typography variant="body2" color="text.secondary">
            No delivery details found for this instructor.
          </Typography>
        </Box>
      );
    }

    return instructor.deliveryDetails.map((detail, index) => (
      <Paper key={index} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Delivery Detail {index + 1}
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(detail).map(([key, value]) => {
            if (key === "id") return null;
            return (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  {key.charAt(0).toUpperCase() +
                    key.slice(1).replace(/([A-Z])/g, " $1")}
                </Typography>
                <Typography variant="body2">
                  {formatDeliveryDetailValue(value)}
                </Typography>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    ));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <ModalDialogTitle onClose={onClose}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" component="span">
            Edit Archived Instructor: {instructor?.name}
          </Typography>
          <Chip
            label={`ID: ${instructor?.id}`}
            variant="outlined"
            size="small"
          />
        </Box>
      </ModalDialogTitle>
      <DialogContent>
        <Box sx={{ maxHeight: "70vh", overflowY: "auto" }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Basic Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="First Name"
                    type="text"
                    fullWidth
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Last Name"
                    type="text"
                    fullWidth
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Phone"
                    type="text"
                    fullWidth
                    value={phone}
                    onChange={handlePhoneChange(setPhone)}
                    placeholder="(xxx) xxx-xxxx"
                    inputProps={{ maxLength: 14 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Ext"
                    type="text"
                    fullWidth
                    value={ext}
                    onChange={(e) => setExt(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Date of Birth"
                    type="date"
                    fullWidth
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  {sitesLoading ? (
                    <TextField
                      margin="dense"
                      label="Site"
                      fullWidth
                      disabled
                      value="Loading sites..."
                    />
                  ) : (
                    <TextField
                      select
                      margin="dense"
                      label="Site"
                      fullWidth
                      value={siteId}
                      onChange={handleSiteChange}
                    >
                      <option value="">No Site</option>
                      {sites.map((s) => (
                        <MenuItem key={s.id} value={s.SiteID || s.id}>
                          {s.SiteName || s.SiteID}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {sitesLoading ? (
                    <TextField
                      margin="dense"
                      label="Partner"
                      fullWidth
                      disabled
                      value="Loading partners..."
                    />
                  ) : (
                    <TextField
                      select
                      margin="dense"
                      label="Partner"
                      fullWidth
                      value={partner}
                      onChange={(e) => setPartner(e.target.value)}
                    >
                      <MenuItem value="">No Partner</MenuItem>
                      {Array.from(
                        new Set(
                          sites
                            .map((s) => (s.SiteName || s.SiteID || "").trim())
                            .filter(Boolean),
                        ),
                      )
                        .sort()
                        .map((p) => (
                          <MenuItem key={p} value={p}>
                            {p}
                          </MenuItem>
                        ))}
                    </TextField>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {pathwaysLoading ? (
                    <TextField
                      margin="dense"
                      label="Pathway"
                      fullWidth
                      disabled
                      value="Loading pathways..."
                    />
                  ) : (
                    <TextField
                      select
                      margin="dense"
                      label="Pathway"
                      fullWidth
                      value={pathway}
                      onChange={handlePathwayChange}
                    >
                      <MenuItem value="">No Pathway</MenuItem>
                      {pathways.map((pw) => (
                        <MenuItem key={pw.id} value={pw.PathwayID}>
                          {pw.PathwayName}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={String(active) === "-1"}
                        onChange={handleActiveToggle}
                        color="primary"
                      />
                    }
                    label={String(active) === "-1" ? "Active" : "Inactive"}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Instructor Details Section (instructor-only fields) */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Instructor Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={eLearnInstructor}
                        onChange={(e) => setELearnInstructor(e.target.checked)}
                      />
                    }
                    label="eLearn Instructor"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={dentalInstructor}
                        onChange={(e) => setDentalInstructor(e.target.checked)}
                      />
                    }
                    label="Dental Instructor"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={pltwInstructor}
                        onChange={(e) => setPltwInstructor(e.target.checked)}
                      />
                    }
                    label="PLTW Instructor"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={academicInstructor}
                        onChange={(e) => setAcademicInstructor(e.target.checked)}
                      />
                    }
                    label="Academic Instructor"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="SCC ID"
                    type="text"
                    fullWidth
                    value={sccId}
                    onChange={(e) => setSccId(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Sinclair Email"
                    type="email"
                    fullWidth
                    value={sinclairEmail}
                    onChange={(e) => setSinclairEmail(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Notes"
                    type="text"
                    fullWidth
                    multiline
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* User Types Section */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">User Types</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userTypes.staff}
                        onChange={handleUserTypeChange("staff")}
                      />
                    }
                    label="Staff"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userTypes.coordinator}
                        onChange={handleUserTypeChange("coordinator")}
                      />
                    }
                    label="Coordinator"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userTypes.instructor}
                        onChange={handleUserTypeChange("instructor")}
                      />
                    }
                    label="Instructor"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userTypes.admin}
                        onChange={handleUserTypeChange("admin")}
                      />
                    }
                    label="Admin"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userTypes.pathwayManager}
                        onChange={handleUserTypeChange("pathwayManager")}
                      />
                    }
                    label="Pathway Manager"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Delivery Details Section (Read-Only) */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">
                Delivery Details (Read-Only)
                <Chip
                  label={`${instructor?.deliveryDetails?.length || 0} records`}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>{renderDeliveryDetails()}</AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
      <ModalActions
        onSave={handleSubmit}
        saving={loading}
        onDelete={handleDelete}
        deleting={deleteLoading}
      />
    </Dialog>
  );
};

export default EditArchivedInstructor;
